import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useGoogleOAuth } from "../hooks/useGoogleOAuth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Separator } from "../components/ui/separator";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Package, AlertTriangle, Loader2, Lock, UserPlus } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL || "";  // Empty = same origin (relative URLs)

// Google icon SVG
const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

export default function Login() {
  const { login, loginWithToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [isRegister, setIsRegister] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleAuthAvailable, setGoogleAuthAvailable] = useState(false);
  const [systemStatus, setSystemStatus] = useState(null);
  const [backendReady, setBackendReady] = useState(true);
  const [backendError, setBackendError] = useState(null);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: ""
  });

  // Get the intended destination after login
  const nextPath = location.state?.next || "/items";

  // Handle OAuth error from redirect (query string)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const error = params.get('error');
    const domain = params.get('domain');
    
    if (error) {
      let errorMessage = "Google sign-in failed";
      
      switch (error) {
        case 'INVITE_REQUIRED':
          errorMessage = "Access denied. You need an invitation to create an account. Please contact an administrator.";
          break;
        case 'GOOGLE_DOMAIN_NOT_ALLOWED':
        case 'domain_not_allowed':
          errorMessage = domain 
            ? `Access denied. The domain '${domain}' is not allowed. Please sign in with your company Google Workspace account.`
            : "Access denied. This account is not allowed. Please sign in with your company Google Workspace account.";
          break;
        case 'account_deleted':
          errorMessage = "This account has been deleted.";
          break;
        case 'oauth_not_configured':
          errorMessage = "Google sign-in is not configured. Please contact your administrator.";
          break;
        case 'invalid_state':
          errorMessage = "Security validation failed. Please try again.";
          break;
        case 'access_denied':
          errorMessage = "Sign-in was cancelled.";
          break;
        case 'missing_code':
          errorMessage = "Authentication failed. No authorization code received.";
          break;
        case 'token_exchange_failed':
        case 'token_exchange_error':
          errorMessage = "Failed to complete sign-in. Please try again.";
          break;
        default:
          errorMessage = `Google sign-in error: ${error}`;
      }
      
      toast.error(errorMessage, { duration: 6000 });
      
      // Clear the error from URL
      window.history.replaceState({}, document.title, '/login');
    }
  }, [location.search]);

  // Handle successful OAuth
  const handleOAuthSuccess = async (accessToken, userData) => {
    await loginWithToken(accessToken, userData);
    toast.success(`Welcome, ${userData.name || userData.email}!`);
    navigate(nextPath, { replace: true });
  };

  // Handle OAuth error
  const handleOAuthError = (error) => {
    console.error("OAuth error:", error);
    if (error) {
      toast.error(error);
    }
  };

  // Google OAuth hook
  const { startOAuth, loading: googleLoading } = useGoogleOAuth({
    onSuccess: handleOAuthSuccess,
    onError: handleOAuthError
  });

  // Check system status and Google OAuth availability
  useEffect(() => {
    const checkSystem = async () => {
      try {
        // Get system status - this endpoint should ALWAYS return 200
        const statusRes = await axios.get(`${API}/api/auth/system-status`);
        const data = statusRes.data;
        
        // Check for backend_ready flag or error field
        if (data.backend_ready === false || data.error) {
          console.warn("Backend not fully ready:", data);
          setBackendReady(false);
          setBackendError(data.error === "DB_NOT_CONNECTED" 
            ? "Database not connected. Please try again in a moment."
            : data.error === "DB_UNAVAILABLE"
            ? "Database not available. Please try again in a moment."
            : data.debug_error || "Backend initializing...");
          // Still use the status values if provided
          setSystemStatus({ 
            activated: data.activated || false, 
            has_admin: data.has_admin || false, 
            invite_only: data.invite_only || true 
          });
          setGoogleAuthAvailable(data.google_oauth_configured || false);
          return;
        }
        
        setBackendReady(true);
        setBackendError(null);
        setSystemStatus(data);
        setGoogleAuthAvailable(data.google_oauth_configured || false);
      } catch (error) {
        // Network error or actual 500 - show degraded mode
        console.error("System status check failed:", error);
        setBackendReady(false);
        const errorMsg = error.response?.data?.debug_error 
          || error.response?.data?.error 
          || error.message 
          || "Backend not reachable";
        setBackendError(`Connection failed: ${errorMsg}`);
        // Set safe defaults - allow password login attempt
        setSystemStatus({ activated: false, has_admin: false, invite_only: true });
        setGoogleAuthAvailable(false);
      }
    };
    checkSystem();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (isRegister) {
        // Registration always requires an invite now
        if (systemStatus?.invite_only) {
          toast.error("Registration requires an invitation. Please use your invite link.");
          setLoading(false);
          return;
        }
        
        await axios.post(`${API}/api/auth/register`, {
          email: formData.email,
          password: formData.password,
          name: formData.name
        });
        
        toast.success("Account created! Please login.");
        setIsRegister(false);
        setFormData({ email: formData.email, password: "", name: "" });
      } else {
        await login(formData.email, formData.password);
        toast.success("Welcome back!");
        navigate(nextPath, { replace: true });
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      let message = "Authentication failed";
      
      if (typeof detail === "object") {
        if (detail.error_code === "INVITE_REQUIRED") {
          message = detail.message || "Registration requires an invitation.";
        } else if (detail.error_code === "DB_UNAVAILABLE") {
          message = "Database not reachable. Please try again later.";
        } else {
          message = detail.message || detail.error_code || "Authentication failed";
        }
      } else if (typeof detail === "string") {
        message = detail;
      }
      
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Determine what to show based on system status
  const showRegistration = false; // Never allow public registration - admin creates users via invites
  const isInviteOnly = systemStatus?.invite_only;

  // No more "System Not Initialized" check - system is always ready if backend is up

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4 relative"
      style={{ background: '#000' }}
    >
      {/* Noise texture overlay */}
      <div className="absolute inset-0 opacity-[0.02] pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`
        }}
      />
      
      <Card className="w-full max-w-md bg-card/95 backdrop-blur-xl border-border rounded-sm">
        <CardHeader className="space-y-4 text-center">
          <div className="flex justify-center">
            <div className="w-16 h-16 bg-primary/10 border border-primary/30 rounded-sm flex items-center justify-center">
              <Package className="w-8 h-8 text-primary" />
            </div>
          </div>
          <div>
            <CardTitle className="text-3xl font-heading tracking-tight">
              KANBAN INVENTORY
            </CardTitle>
            <CardDescription className="text-muted-foreground mt-2">
              {!systemStatus?.activated ? (
                "Setup your admin account to get started"
              ) : isRegister ? (
                "Create your account"
              ) : (
                "Sign in to continue"
              )}
            </CardDescription>
          </div>
          
          {/* System Status Badge */}
          {systemStatus && (
            <div className="flex justify-center">
              {isInviteOnly && !isRegister && (
                <Badge variant="outline" className="border-primary/50 text-primary bg-primary/10">
                  <Lock className="w-3 h-3 mr-1" />
                  Invite Only
                </Badge>
              )}
            </div>
          )}
        </CardHeader>
        
        {/* Backend Not Ready Banner */}
        {!backendReady && backendError && (
          <div className="mx-6 mb-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-sm">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-400 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-yellow-400">Backend Not Ready</p>
                <p className="text-xs text-yellow-400/80 mt-1">{backendError}</p>
              </div>
            </div>
          </div>
        )}
        
        <CardContent>
          {/* Google Sign-In Button - only show on login when system is activated */}
          {googleAuthAvailable && !isRegister && systemStatus?.activated && (
            <>
              <Button
                type="button"
                variant="outline"
                className="w-full h-11 flex items-center justify-center gap-3 rounded-sm"
                onClick={startOAuth}
                disabled={googleLoading}
                data-testid="google-login-btn"
              >
                {googleLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <GoogleIcon />
                )}
                <span>{googleLoading ? "Signing in..." : "Sign in with Google"}</span>
              </Button>
              
              <div className="relative my-6">
                <Separator />
                <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card px-3 text-xs text-muted-foreground uppercase">
                  or continue with email
                </span>
              </div>
            </>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {isRegister && (
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-medium tracking-wide uppercase text-muted-foreground">
                  Name
                </Label>
                <Input
                  id="name"
                  data-testid="register-name-input"
                  type="text"
                  placeholder="Your name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required={isRegister}
                  className="h-11 border-2 border-border/50 focus:border-primary"
                />
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium tracking-wide uppercase text-muted-foreground">
                Email
              </Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                placeholder="you@company.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="h-11 border-2 border-border/50 focus:border-primary"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium tracking-wide uppercase text-muted-foreground">
                Password
              </Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                className="h-11 border-2 border-border/50 focus:border-primary"
              />
            </div>
            
            <Button 
              type="submit" 
              className="w-full h-11 uppercase tracking-wider font-semibold rounded-sm"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : isRegister ? (
                "Create Account"
              ) : (
                "Sign In"
              )}
            </Button>
          </form>
          
          {/* Registration note */}
          <div className="mt-6 text-center">
            {!isRegister ? (
              <p className="text-sm text-muted-foreground">
                Need an account?{" "}
                <span className="text-primary">Contact an administrator for an invitation.</span>
              </p>
            ) : (
              <button
                type="button"
                onClick={() => setIsRegister(false)}
                className="text-sm text-muted-foreground hover:text-primary transition-colors"
                data-testid="toggle-auth-mode-btn"
              >
                Already have an account? Sign in
              </button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
